import toast from 'react-hot-toast'
import { useLocalStorage } from 'usehooks-ts'

import { Logger } from '@shared/utils'

const log = Logger('useFlaggedHistory')

const STORAGE_KEY = 'flagged-history-v1'
const LEGACY_IDB_NAME = 'careplus-flagged'
const LEGACY_IDB_STORE = 'flagged'

// Unlike pages history (which silently drops its oldest entry past the limit),
// flags are user-intentional, so we cap the count by refusing new flags and
// telling the user, rather than evicting one they chose to keep.
const FLAGGED_LIMIT = 50

export const getFlaggedHistoryId = (type, id) => `${type}-${id}`

export const FlaggedHistoryType = {
  Patient: 'patient',
}

const pickFlaggedData = (type, data) => {
  if (!data) return data

  switch (type) {
    case FlaggedHistoryType.Patient:
      return {
        id: data.id,
        fullName: data.fullName,
        firstName: data.firstName,
        lastName: data.lastName,
        profilePictureThumbUrl: data.profilePictureThumbUrl,
      }
    default:
      return data
  }
}

// Read every value out of the old store, then CLOSE the connection — idb-keyval
// keeps its connection cached/open, which would block the deleteDatabase below,
// so we use raw IndexedDB and close it ourselves.
const readLegacyFlags = (db) =>
  new Promise((resolve) => {
    const done = (result) => {
      db.close()
      resolve(result)
    }
    if (!db.objectStoreNames.contains(LEGACY_IDB_STORE)) return done([])

    const request = db.transaction(LEGACY_IDB_STORE, 'readonly').objectStore(LEGACY_IDB_STORE).getAll()
    request.onsuccess = () => done(request.result || [])
    request.onerror = () => done([])
  })

const openLegacyDb = () =>
  new Promise((resolve, reject) => {
    const request = window.indexedDB.open(LEGACY_IDB_NAME)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

// One-time migration: pull flags out of the superseded careplus-flagged
// IndexedDB store into the localStorage store (slimming their data), then
// delete the old database. Async because reading IndexedDB is; the store's
// existence is the signal to run, so no separate flag is needed.
const migrateFlaggedHistory = async () => {
  if (typeof window === 'undefined' || !('indexedDB' in window)) return

  try {
    // Don't open (and thereby recreate) the old DB unless it actually exists.
    if (window.indexedDB.databases) {
      const dbs = await window.indexedDB.databases()
      if (!dbs.some((db) => db.name === LEGACY_IDB_NAME)) return
    }

    const legacyItems = (await readLegacyFlags(await openLegacyDb())).filter(Boolean)

    if (legacyItems.length > 0) {
      const incoming = legacyItems
        .map((item) => ({ ...item, data: pickFlaggedData(item.type, item.data) }))
        .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))

      const existing = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]')
      const seen = new Set()
      const merged = []
      for (const item of [...existing, ...incoming]) {
        if (item && !seen.has(item.id)) {
          seen.add(item.id)
          merged.push(item)
        }
      }

      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged.slice(0, FLAGGED_LIMIT)))
      // Let any mounted useLocalStorage(STORAGE_KEY) instances pick this up.
      window.dispatchEvent(new StorageEvent('local-storage', { key: STORAGE_KEY }))
    }

    window.indexedDB.deleteDatabase(LEGACY_IDB_NAME)
  } catch (e) {
    // If anything fails, delete the old database anyway so a broken read can't
    // wedge the migration (and re-run it) on every load.
    log.error('Failed to migrate flagged history; deleting legacy store:', e)
    window.indexedDB.deleteDatabase(LEGACY_IDB_NAME)
  }
}

// Fire-and-forget: the migration runs once in the background on load.
void migrateFlaggedHistory()

export default function useFlaggedHistory() {
  const [items, setItems] = useLocalStorage(STORAGE_KEY, [])

  const toggle = (type, data) => {
    if (!type || !data) return

    const id = getFlaggedHistoryId(type, data.id)
    const alreadyFlagged = items.some((item) => item.id === id)

    // Adding a new flag is capped; un-flagging an existing one is always allowed.
    if (!alreadyFlagged && items.length >= FLAGGED_LIMIT) {
      toast.error(`You can flag up to ${FLAGGED_LIMIT} patients. Unflag one to add another.`)
      return
    }

    try {
      setItems((prev) => {
        if (prev.some((item) => item.id === id)) return prev.filter((item) => item.id !== id)

        const item = { id, createdAt: new Date().toISOString(), type, data: pickFlaggedData(type, data) }
        return [item, ...prev]
      })
    } catch (e) {
      // The shared localStorage bucket can fill from other data before the
      // count cap is reached; surface that instead of failing silently. (Only
      // adding can exceed the quota — un-flagging shrinks storage.)
      if (e.name === 'QuotaExceededError') {
        toast.error("Couldn't flag this patient - your browser storage is full. Unflag one to add another.")
      } else {
        log.error('Error toggling flag:', e)
      }
    }
  }

  const clear = () => setItems([])

  const isFlagged = (type, data) => {
    if (!type || !data) return false
    const id = getFlaggedHistoryId(type, data.id)
    return items.some((item) => item.id === id)
  }

  return { items, toggle, clear, isFlagged }
}
